import PhotoSwipe from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipeVideoPlugin from 'photoswipe-video-plugin';
import { SWIPER } from '../../utils/constants';

let lightbox = null;
let isLightboxInitialized = false;

const buildSlideData = (slideEl) => {
  const { lightboxImage, lightboxVideoSrc, lightboxIframeSrc, lightboxIframeTitle, lightboxWidth, lightboxHeight, lightboxPoster } = slideEl.dataset;
  const width = Number(lightboxWidth);
  const height = Number(lightboxHeight);
  if (!width || !height) return null;

  if (lightboxIframeSrc) {
    return { type: 'iframe', iframeSrc: lightboxIframeSrc, iframeTitle: lightboxIframeTitle, width, height, msrc: lightboxPoster };
  }

  if (lightboxVideoSrc) {
    return { type: 'video', videoSrc: lightboxVideoSrc, width, height, msrc: lightboxPoster };
  }

  if (lightboxImage) {
    const slideImageEl = slideEl.querySelector('img');
    return { src: lightboxImage, width, height, msrc: slideImageEl?.currentSrc };
  }

  return null;
};

// Pause any embedded iframe video when the lightbox is opened so it doesn't play under the lightbox.
const pauseEmbeddedIframe = (iframeEl) => {
  if (!iframeEl.contentWindow) return;

  if (iframeEl.src.includes('youtube.com')) {
    iframeEl.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
      'https://www.youtube.com',
    );
  }
};

const getLightbox = () => {
  if (lightbox) return lightbox;

  lightbox = new PhotoSwipeLightbox({ pswpModule: PhotoSwipe });
  // the plugin registers itself onto the lightbox via events — no reference needed
  new PhotoSwipeVideoPlugin(lightbox, {});

  // iframe support
  lightbox.on('contentLoad', (e) => {
    const { content } = e;
    if (content.data.type !== 'iframe') return;

    e.preventDefault();

    const iframeEl = document.createElement('iframe');
    iframeEl.src = content.data.iframeSrc;
    iframeEl.width = content.width;
    iframeEl.height = content.height;
    iframeEl.style.width = `${content.width}px`;
    iframeEl.style.height = `${content.height}px`;
    iframeEl.style.border = 'none';
    iframeEl.setAttribute('allowfullscreen', '');
    iframeEl.setAttribute('allow', 'autoplay; fullscreen');
    iframeEl.setAttribute('title', content.data.iframeTitle || 'Product video');

    content.element = iframeEl;
    content.onLoaded();
  });

  lightbox.on('contentDeactivate', (e) => {
    const { content } = e;
    if (content.data.type !== 'iframe') return;
    if (content.element) pauseEmbeddedIframe(content.element);
  });

  lightbox.on('contentDestroy', (e) => {
    const { content } = e;
    if (content.data.type !== 'iframe') return;
    // drop the src to stop playback/audio when the slide is gone
    if (content.element) content.element.src = '';
  });

  lightbox.init();

  return lightbox;
};

const openGalleryLightbox = (galleryEl) => {
  const slideEls = [...galleryEl.querySelectorAll(SWIPER.SLIDE_CLASS)];
  const slides = slideEls
    .map((slideEl) => ({ slideEl, data: buildSlideData(slideEl) }))
    .filter((slide) => slide.data);
  if (!slides.length) return;

  const activeIndex = slides.findIndex(
    (slide) => slide.slideEl.classList.contains('swiper-slide-active'),
  );

  // Pause any inline gallery video so it doesn't play under the lightbox.
  const videoEls = galleryEl.querySelectorAll('video');
  videoEls.forEach((videoEl) => videoEl.pause());

  // Pause any inline gallery iframe embeds the same way.
  const iframeEls = galleryEl.querySelectorAll('iframe');
  iframeEls.forEach((iframeEl) => pauseEmbeddedIframe(iframeEl));

  const lightboxInstance = getLightbox();
  lightboxInstance.loadAndOpen(Math.max(activeIndex, 0), slides.map((slide) => slide.data));
};

const initProductGalleryLightbox = () => {
  if (isLightboxInitialized) return;

  document.addEventListener('click', (event) => {
    const triggerEl = event.target.closest('[data-product-media-lightbox-trigger]');
    if (!triggerEl) return;

    const galleryEl = triggerEl.closest('[data-product-gallery-media]');
    if (!galleryEl) return;

    openGalleryLightbox(galleryEl);
  });

  isLightboxInitialized = true;
};

export default initProductGalleryLightbox;
